import { AxiosResponse } from "axios";
import { CartTotal } from "../common/features/cart/cartModels";
import { manageItemTotalsApi } from "../common/features/order/orderAPI";
import { ItemTotalsPayloadInteface, ItemTotalsResponseInteface, OrderTotal } from "../common/features/order/orderModels";
import { toast } from "react-toastify";
import { decryptJson, handleDecrypt } from "../common/utils/react-ec-utils";
import { setTotals, setTotalsConfirmedOfferIds, reconcileItemOffer } from "../common/features/cart/cartActions";
import { showErrorToast, showErrorToastDesktop } from "../common/utils/toastUtils";
import { clearAppliedCoupon } from "../common/features/restaurant/restaurantAction";
import { dismissOfferAtSubtotal } from "../common/utils/offer-utils";
import { setSignupPopup } from "../common/features/navigation/navigationActions";
import { getCookie } from "./cookieHelper";
import { USER_ID } from "../common/constants";
import { replacecartItems } from "../common/features/cart/cartActions";
import { RestaurantMenuItem } from "../common/features/restaurant/restaurantModels";

const toNumber = (value: any, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const toQuantityString = (value: any, fallback = 1) => {
  const parsed = Math.max(0, Math.floor(toNumber(value, fallback)));
  return String(parsed);
};

const toMoneyString = (value: any, fallback = 0) => {
  return toNumber(value, fallback).toFixed(2);
};

const getGroupedOptions = (options: any[] = []) => {
  const grouped = new Map<string, any>();
  options.forEach((option: any) => {
    const key = option?.modifierOptionId
      ? String(option.modifierOptionId)
      : `${String(option?.optionName || "")}__${String(option?.price || "")}`;
    const qty = Math.max(0, Math.floor(toNumber(option?.quantity, 1)));
    if (qty <= 0) {
      return;
    }

    const existing = grouped.get(key);
    if (existing) {
      grouped.set(key, {
        ...existing,
        quantity: toQuantityString(toNumber(existing?.quantity, 1) + qty, 1),
      });
      return;
    }

    grouped.set(key, {
      ...option,
      quantity: toQuantityString(qty, 1),
      price: toMoneyString(option?.price, 0),
    });
  });
  return Array.from(grouped.values());
};

const getOptionsAmount = (options: any[] = []) => {
  return options.reduce((total: number, option: any) => {
    const optionPrice = toNumber(option?.price, 0);
    const optionQty = Math.max(0, Math.floor(toNumber(option?.quantity, 1)));
    return total + optionPrice * optionQty;
  }, 0);
};

const sanitizeTotalsPayload = (payload: ItemTotalsPayloadInteface) => {

  const orderItemsArr = (payload as any)?.orderItems || [];
  // True when any cart entry carries an item-level menu offer (top-level
  // `offerId`, e.g. a "% Off" on an item). Flagged onto BOGO get items below
  // via `isItemLevelOfferApplied` so the server knows the BOGO get item
  // coexists with an item-level offer.
  const hasItemLevelOffer = orderItemsArr.some(
    (i: any) => i?.offerId && Number(i?.quantity || 0) > 0
  );

  return {
    ...payload,
    orderItems: orderItemsArr.map((item: any) => {
      const quantityNum = Math.max(1, toNumber(item?.quantity, 1));
      const subTotalNum = toNumber(item?.subTotal, 0);
      const fallbackUnitPrice = quantityNum > 0 ? subTotalNum / quantityNum : subTotalNum;
      const unitPriceNum = toNumber(item?.price, fallbackUnitPrice);

      const optionTemplates = Array.isArray(item?.options) ? item.options : [];
      const selectedModifierIds = String(item?.modifierIds || "")
        .split(",")
        .map((id) => id.trim())
        .filter((id) => id && id !== "0");
      const selectedModifierCounts = selectedModifierIds.reduce(
        (counts: Record<string, number>, id: string) => {
          counts[id] = (counts[id] || 0) + 1;
          return counts;
        },
        {}
      );
      const hasModifierSelection = Object.keys(selectedModifierCounts).length > 0;

      const optionsFromModifierIds = hasModifierSelection
        ? (() => {
            const templateByModifierId = optionTemplates.reduce(
              (acc: Record<string, any>, option: any) => {
                if (option?.modifierOptionId && !acc[option.modifierOptionId]) {
                  acc[option.modifierOptionId] = option;
                }
                return acc;
              },
              {}
            );

            const groupedOptions: any[] = [];
            for (const [modifierOptionId, count] of Object.entries(selectedModifierCounts)) {
              const template = templateByModifierId[modifierOptionId];
              if (!template) {
                return null;
              }
              const normalizedPrice = toMoneyString(template?.price, 0);
              groupedOptions.push({
                ...template,
                modifierOptionId,
                quantity: toQuantityString(toNumber(count, 1) * quantityNum, 1),
                price: normalizedPrice,
              });
            }
            return groupedOptions;
          })()
        : null;
      const groupedOptionsFromTemplates = getGroupedOptions(optionTemplates);
      const normalizedOptions = (() => {
        if (!optionsFromModifierIds) {
          return groupedOptionsFromTemplates;
        }
        if (groupedOptionsFromTemplates.length === 0) {
          return optionsFromModifierIds;
        }

        const targetSubTotal = Math.max(0, subTotalNum);
        const baseAmount = unitPriceNum * quantityNum;
        const amountFromModifierIds = baseAmount + getOptionsAmount(optionsFromModifierIds);
        const amountFromTemplates = baseAmount + getOptionsAmount(groupedOptionsFromTemplates);

        return Math.abs(targetSubTotal - amountFromTemplates) <
          Math.abs(targetSubTotal - amountFromModifierIds)
          ? groupedOptionsFromTemplates
          : optionsFromModifierIds;
      })();

      // BOGO get items (discounted via a BOGO offer) carry the
      // `isItemLevelOfferApplied` flag so the server knows whether they
      // coexist with an item-level offer on the cart.
      const isBogoGetItem = item?.offer_data?.isBogoFreeItem === true;

      return {
        ...item,
        ...(isBogoGetItem ? { isItemLevelOfferApplied: hasItemLevelOffer } : {}),
        quantity: toQuantityString(item?.quantity, 1),
        subTotal: toMoneyString(item?.subTotal, 0),
        price: toMoneyString(item?.price, unitPriceNum),
        offer_amount: toNumber(item?.offer_amount, 0),
        is_discount_applied: !!item?.is_discount_applied,
        options:
          optionsFromModifierIds ||
          getGroupedOptions(optionTemplates),
      };
    }),
  };
};

// Build a copy of the totals payload with `offerId` stripped from every place
// it can appear (item-level stamp, offerCaptureRequests, offerIds), so the
// request can be retried immediately after the server rejects that offer.
// Item-level entries are reverted to their pre-offer price using the same
// `qty × (price + Σ modifier_price × modifier_qty)` math as the
// RECONCILE_ITEM_OFFER reducer — otherwise the retry would send the discounted
// price with no offer attached and the server would total the order too low.
const stripOfferFromTotalsPayload = (payload: any, offerId: any) => {
  const id = String(offerId ?? '');
  if (!id) return payload;

  const orderItems = (Array.isArray(payload?.orderItems) ? payload.orderItems : []).map(
    (item: any) => {
      if (!item?.offerId || String(item.offerId) !== id) return item;
      const qty = Math.max(0, toNumber(item?.quantity, 1));
      const basePrice = toNumber(item?.originalPrice ?? item?.price, 0);
      const modifiers = (Array.isArray(item?.options) ? item.options : []).reduce(
        (sum: number, option: any) => {
          const optionQty = toNumber(option?.quantity, 1) > 0 ? toNumber(option?.quantity, 1) : 1;
          return sum + toNumber(option?.price, 0) * optionQty;
        },
        0
      );
      return {
        ...item,
        offerId: null,
        price: toMoneyString(basePrice),
        originalPrice: toMoneyString(basePrice),
        subTotal: toMoneyString(qty * (basePrice + modifiers)),
        offer_amount: 0,
        is_discount_applied: false,
      };
    }
  );

  const next: any = { ...payload, orderItems };
  if (Array.isArray(payload?.offerCaptureRequests)) {
    next.offerCaptureRequests = payload.offerCaptureRequests.filter(
      (request: any) => String(request?.offerId ?? '') !== id
    );
  }
  if (Array.isArray(payload?.offerIds)) {
    next.offerIds = payload.offerIds.filter((value: any) => String(value ?? '') !== id);
  }
  return next;
};

// Upper bound on the "strip the rejected offer and retry" chain, so a cart
// holding several not-yet-started offers still resolves while a server that
// keeps rejecting can never spin the totals API forever.
const MAX_OFFER_STRIP_RETRIES = 3;

let totalsRequestSeq = 0;

let _totalsPendingResolve: ((v: null) => void) | null = null;
let _totalsDebounceTimer: ReturnType<typeof setTimeout> | null = null;

// Once the user commits to paying, totals must never be recalculated —
// a debounced totals call racing the payment API can 400/mutate applied
// offers mid-payment, or simply waste a request after the order is placed.
let _isPaymentInFlight = false;
let _paymentInFlightSafetyTimer: ReturnType<typeof setTimeout> | null = null;

export const setTotalsPaymentInFlight = (value: boolean) => {
  _isPaymentInFlight = value;
  if (_paymentInFlightSafetyTimer) {
    clearTimeout(_paymentInFlightSafetyTimer);
    _paymentInFlightSafetyTimer = null;
  }
  if (value) {
    if (_totalsDebounceTimer) {
      clearTimeout(_totalsDebounceTimer);
      _totalsDebounceTimer = null;
    }
    if (_totalsPendingResolve) {
      _totalsPendingResolve(null);
      _totalsPendingResolve = null;
    }
    // Safety valve: if some payment error/bail-out path fails to clear this
    // flag explicitly, don't leave totals recalculation stuck forever.
    _paymentInFlightSafetyTimer = setTimeout(() => {
      _isPaymentInFlight = false;
      _paymentInFlightSafetyTimer = null;
    }, 20000);
  }
};

const debouncedTotalsApi = (
  sanitizedPayload: ItemTotalsPayloadInteface
): Promise<AxiosResponse | null> => {
  if (_totalsPendingResolve) {
    _totalsPendingResolve(null);
    _totalsPendingResolve = null;
  }
  if (_totalsDebounceTimer) {
    clearTimeout(_totalsDebounceTimer);
    _totalsDebounceTimer = null;
  }
  return new Promise((resolve) => {
    _totalsPendingResolve = resolve;
    _totalsDebounceTimer = setTimeout(async () => {
      _totalsPendingResolve = null;
      _totalsDebounceTimer = null;
      if (_isPaymentInFlight) {
        resolve(null);
        return;
      }
      const result = await manageItemTotalsApi(sanitizedPayload);
      resolve(result);
    }, 300);
  });
};

export const manageItemTotalsHelper = async (payload: ItemTotalsPayloadInteface, dispatch?: any, _appliedCoupons?: any[], _offerStripRetryDepth: number = 0): Promise<any> => {
    if (_isPaymentInFlight) return null;
    let totalsData: ItemTotalsResponseInteface|any = null;
    const isMobile = window.innerWidth <= 768;
    const requestSeq = ++totalsRequestSeq;
    try {
      const sanitizedPayload = sanitizeTotalsPayload(payload);
      const response: AxiosResponse | null = await debouncedTotalsApi(sanitizedPayload);
      if (!response) return null;

      if (requestSeq !== totalsRequestSeq) {
        return null;
      }

      if (response?.status == 200 && response?.data?.encryptedText) {
        const data = decryptJson(response.data.encryptedText)
        totalsData = data as ItemTotalsResponseInteface;
        if (dispatch) {
          dispatch(setTotals(totalsData));
          // Signal which offer IDs were confirmed valid by this response so
          // the popup queue only celebrates after the server accepts the offer.
          const confirmedIds: string[] = ((sanitizedPayload as any)?.offerCaptureRequests ?? [])
            .map((r: any) => r?.offerId)
            .filter(Boolean);
          dispatch(setTotalsConfirmedOfferIds(confirmedIds));
        }
      } else {
        console.log({response},response?.data);

        // FIRST_TIME_ONLY: the offer requires a verified customer profile.
        // • Logged out → remove the offer + open login popup (not dismissed, so
        //   user can re-apply after signing in).
        // • Logged in  → dismiss the offer for this session + show a toast.
        //   Permanent dismissal breaks the auto-apply re-queue loop that would
        //   otherwise trigger rapid subsequent totals failures.
        if (response?.status == 400 && response?.data?.reasonCode === 'FIRST_TIME_ONLY') {
          const offerId = response?.data?.offerId;
          const isLoggedIn = !!getCookie(USER_ID);
          if (dispatch) {
            dispatch(setTotalsConfirmedOfferIds([]));
            dispatch(clearAppliedCoupon(offerId ? { id: offerId } : undefined));
            if (isLoggedIn) {
              if (offerId) dismissOfferAtSubtotal(offerId, Number.MAX_SAFE_INTEGER);
              // const msg = response?.data?.message || 'This offer is only valid for first-time orders.';
              const msg = 'Some offers are no longer available. Your order has been updated.';

              isMobile ? showErrorToast(msg) : showErrorToastDesktop(msg);
            } else {
              dispatch(setSignupPopup(true));
            }
          }
          return null;
        }

         // OFFER_NOT_STARTED: the offer's start date hasn't been reached yet
         // (typically a start-day / restaurant-timezone boundary). Treated like
         // the other server rejections — the offer is removed from the cart
         // whatever its category (item-level via reconcileItemOffer below,
         // ORDER / FREE_ITEM / FREE_DELIVERY / BOGO via clearAppliedCoupon by
         // id) — and totals are then recalculated without it.
         if (response?.status == 400 && (response?.data?.reasonCode === 'OFFER_INACTIVE' || response?.data?.reasonCode === 'MAX_CAP_REACHED' || response?.data?.reasonCode === 'PER_CUSTOMER_LIMIT_EXCEEDED' || response?.data?.reasonCode === 'ORDER_TYPE_MISMATCH' || response?.data?.reasonCode === 'OFFER_NOT_STARTED')) {

        const offerId = response?.data?.offerId;
        // Dismiss at a very large subtotal so the auto-apply watcher treats
        // this server-rejected offer as permanently dismissed for the session.
        if (offerId) dismissOfferAtSubtotal(offerId, Number.MAX_SAFE_INTEGER);
        if (dispatch) {
          dispatch(setTotalsConfirmedOfferIds([]));
          dispatch(clearAppliedCoupon(offerId ? { id: offerId } : undefined));
          // For item-level offers the offerId lives on the cart item (not in
          // appliedCoupons), so clearAppliedCoupon alone won't trigger a Redux
          // state change → the totals useEffect never re-fires. Clearing the
          // offerId from matching cart items changes cartItems and re-triggers
          // the effect so totals are recalculated without the dismissed offer.
          if (offerId && Array.isArray((payload as any).orderItems)) {
            (payload as any).orderItems.forEach((item: any) => {
              if (item?.offerId && String(item.offerId) === String(offerId) && item?.itemId) {
                dispatch(reconcileItemOffer({
                  itemId: String(item.itemId),
                  apply: false,
                  price: item.price ?? 0,
                }));
              }
            });
          }
        }
        let offerErrorMsg: string;
        // OFFER_NOT_STARTED's server message is technical ("Valid from
        // 2026-08-11 00:00:00.0"), so show the standard removal copy instead.
        if (
          response?.data?.reasonCode === 'ORDER_TYPE_MISMATCH' ||
          response?.data?.reasonCode === 'OFFER_NOT_STARTED'
        ) {
          offerErrorMsg = 'Some offers are no longer available. Your order has been updated.';
        } else {
          offerErrorMsg = response?.data?.message || 'This offer is not applicable today';
        }
        const offerErrorDuration = offerErrorMsg === 'Some offers are no longer available. Your order has been updated.' ? 3500 : undefined;
        if (isMobile) {
          showErrorToast(offerErrorMsg, 'totals-offer-error', offerErrorDuration);
        } else {
          showErrorToastDesktop(offerErrorMsg, 'totals-offer-error', offerErrorDuration);
        }

        // Recalculate immediately with the rejected offer stripped out. The
        // Redux cleanup above also re-fires the totals effect, but only once
        // the cart-watcher has reverted the item — until then the UI would keep
        // showing the stale totals that still include the rejected discount.
        if (
          response?.data?.reasonCode === 'OFFER_NOT_STARTED' &&
          offerId &&
          _offerStripRetryDepth < MAX_OFFER_STRIP_RETRIES
        ) {
          return await manageItemTotalsHelper(
            stripOfferFromTotalsPayload(payload, offerId),
            dispatch,
            _appliedCoupons,
            _offerStripRetryDepth + 1
          );
        }
        return null;
      }

      // Exclusive offer conflict: the server rejected the request because an
      // exclusive item-level offer in the cart can't coexist with order-level /
      // free-item coupons. Remove all coupon-level applied offers so the totals
      // useEffect re-fires automatically with a clean offer slate.
      const responseMsg: string = String(
        response?.data?.message ?? response?.data?.reasonCode ?? ''
      ).toLowerCase();
      if (
        response?.status == 400 &&
        (response?.data?.reasonCode === 'EXCLUSIVE_OFFER' ||
          responseMsg.includes('exclusive offer') ||
          responseMsg.includes('exclusive'))
      ) {
        if (dispatch) {
          dispatch(setTotalsConfirmedOfferIds([]));
          dispatch(clearAppliedCoupon({ category: 'ORDER' }));
          dispatch(clearAppliedCoupon({ category: 'FREE_ITEM' }));
          dispatch(clearAppliedCoupon({ category: 'FREE_DELIVERY' }));
        }
        if (isMobile) {
          showErrorToast('Some offers are no longer available. Your order has been updated.', 'totals-exclusive-error', 3500);
        } else {
          showErrorToastDesktop('Some offers are no longer available. Your order has been updated.', 'totals-exclusive-error', 3500);
        }
        return null;
      }
        // errorNotify('Unable to process');
      }
    } catch {
    }
    // if (payload?.sagaCallBack != null && typeof payload?.sagaCallBack === 'function') {
    //   payload.sagaCallBack();
    // }
    return totalsData;
  };

  // Recomputes cart line prices/subtotals from a freshly-fetched menu (item price changes,
  // modifier options that no longer exist) and writes the result back to state.cart.cart.items.
  // Same reconciliation MenuPage's commonSuccess does on menu refresh.
  export const syncCartItemPricesFromMenu = (
    menuItems: RestaurantMenuItem[],
    cartItems: any[],
    dispatch?: any
  ) => {
    const menuItemMap = new Map((menuItems || []).map((item) => [item.itemId, item]));

    const updatedCartItems = (cartItems || []).map((cartItem: any) => {
      const matchedMenuItem = menuItemMap.get(cartItem.itemId);
      if (!matchedMenuItem) return cartItem;

      // Customization comes back either as flat options (legacy) or as modifier
      // groups with a nested `options` array (current format, see menuDetails
      // isNewFormat check). Flatten both shapes here — otherwise every group's
      // top-level entry has no modifierOptionId of its own, the lookup below
      // ends up keyed only on `undefined`, and every real modifier gets stripped.
      const customization: any[] = matchedMenuItem.customization || [];
      const isGroupedFormat =
        customization[0]?.modifierId != null && Array.isArray(customization[0]?.options);
      // Map each still-valid option id to its CURRENT menu option, so surviving
      // modifiers can be re-priced below — not just filtered. Keeping the cart's
      // own stale `opt.price` here is what let a modifier price change show the
      // old line price while the (server-priced) Bill Details total moved.
      const menuOptionById = new Map<any, any>(
        (isGroupedFormat
          ? customization.flatMap((modifier: any) => modifier.options || [])
          : customization
        ).map((opt: any) => [opt.modifierOptionId, opt])
      );

      const filteredOptions = (cartItem.options || [])
        .filter((opt: any) => menuOptionById.has(opt.modifierOptionId))
        .map((opt: any) => {
          const menuOption = menuOptionById.get(opt.modifierOptionId);
          return { ...opt, price: Number(menuOption?.price) || 0 };
        });

      // Multi-quantity modifiers ("2x Onion") cost price × quantity; an absent
      // or zero quantity means a single selection.
      const optionTotal = filteredOptions.reduce((sum: number, opt: any) => {
        const optQty = Number(opt?.quantity) > 0 ? Number(opt.quantity) : 1;
        return sum + (Number(opt?.price) || 0) * optQty;
      }, 0);

      const newPrice = parseFloat(matchedMenuItem.price);
      const quantity = parseFloat(cartItem.quantity);
      const newSubTotal = ((newPrice + optionTotal) * quantity).toFixed(2);

      return {
        ...cartItem,
        price: newPrice,
        subTotal: newSubTotal,
        options: filteredOptions,
      };
    });

    if (dispatch) {
      dispatch(replacecartItems(updatedCartItems));
    }
    return updatedCartItems;
  };

  export const successNotify = (message: string) =>
  toast.success(message, {
    position: "top-center",
  });

  export const errorNotify = (message: string) =>
    showErrorToastDesktop(message);


  export const getTotalByTitle = (totals: any, title: string) => {
    let filteredData = totals?.filter((total: any) => total.title == title);
    if (filteredData.length > 0) {
      return filteredData[0].value;
    } else {
      return 0;
    }
  };

  export const getTotalByCode = (
    totals: CartTotal[] | OrderTotal[],
    code: string,
  ): CartTotal | OrderTotal | null => {
    let totalIndex = totals?.findIndex((total) => Number(+total.code) == Number(+code));
    if (totalIndex >= 0) {
      return totals[totalIndex];
    } else {
      return null;
    }
  };

  export const getTitleByCode = (
    totals: CartTotal[] | OrderTotal[],
    code: string,
  ): CartTotal | OrderTotal | null => {
    let totalIndex = totals?.findIndex((total) => Number(+total.code) == Number(+code));
    if (totalIndex >= 0) {
      return totals[totalIndex];
    } else {
      return null;
    }
  };
